import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { DesignShowcase } from "../../../../api/graphql";
import { DesignShowcaseListStore } from "../../store";
import { showcaseCategoryDisplayId } from "../../../showcaseCategory/store/ShowcaseCategoryCommonStore";
import { showcaseWorkflowStateDisplayId } from "../../../showcaseWorkflowState/store/ShowcaseWorkflowStateCommonStore";
import { showcaseLineDisplayId } from "../../../showcaseLine/store/ShowcaseLineCommonStore";
import { diaDeCalendario, formatearDia } from "../../../../common/calendarDate";

import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
} from "../../../../icons";

export type GridExpressProps = { listStore?: DesignShowcaseListStore };

export const DesignShowcaseListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<DesignShowcase>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: DesignShowcase, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: DesignShowcase, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: DesignShowcase) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: DesignShowcase, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<DesignShowcase>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "status") {
          return {
            ...col,
            id: "status",
            accessorFn: (row: DesignShowcase) => {
              const v = (row as any).status;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.ShowcaseStatusOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "category") {
          return {
            ...col,
            id: "category",
            accessorFn: (row: DesignShowcase) =>
              row.category
                ? showcaseCategoryDisplayId(row.category, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "relatedCategories") {
          return {
            ...col,
            id: "relatedCategories",
            accessorFn: (row: DesignShowcase) =>
              row.relatedCategories?.length
                ? row.relatedCategories
                    .map((x: any) => showcaseCategoryDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "appliedStatuses") {
          return {
            ...col,
            id: "appliedStatuses",
            accessorFn: (row: DesignShowcase) => {
              const v = (row as any).appliedStatuses;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.ShowcaseStatusOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "lifecycle") {
          return {
            ...col,
            id: "lifecycle",
            accessorFn: (row: DesignShowcase) =>
              row.lifecycle
                ? showcaseWorkflowStateDisplayId(row.lifecycle, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "startDate") {
          return {
            ...col,
            id: "startDate",
            accessorFn: (row: DesignShowcase) =>
              formatearDia(
                diaDeCalendario(row.startDate),
                langStore.timeFormat
              ) || "-",
          };
        }
        if (col.accessorKey === "expiresAt") {
          return {
            ...col,
            id: "expiresAt",
            accessorFn: (row: DesignShowcase) =>
              row.expiresAt
                ? new Date(row.expiresAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "registeredAt") {
          return {
            ...col,
            id: "registeredAt",
            accessorFn: (row: DesignShowcase) =>
              row.registeredAt
                ? new Date(row.registeredAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "lines") {
          return {
            ...col,
            id: "lines",
            accessorFn: (row: DesignShowcase) =>
              row.lines?.length
                ? row.lines
                    .map((x: any) => showcaseLineDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.designShowcaseCurrentPage || [];

    return (
      <div id={"ListOfDesignShowcase"}>
        <div id={"StartOfListingOfDesignShowcase"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
